import type { NavSubject } from './nav'

/**
 * Where `/` sends a signed-in person (D-370, amended by D-384).
 *
 * **Two rungs, because the middle ones were the bug.** It used to route by
 * whether she taught: dual-role to `/home`, teacher-only to `/teacher`,
 * everyone else to `/me`. Those two single-role screens were the leftovers of
 * the portal switch D-331 retired, and they had rotted — measured, `/teacher`
 * rendered **103 characters and no heading at all** for a teacher with
 * twenty-one classes, and `/me` had lost the parent's next-class answer to a
 * panel that only `/home` carries.
 *
 * **`/home` was already right for all four account shapes.** It composes from
 * `teaches` and `isParent` and renders each half only when there is one, so
 * nothing had to be built — the ladder simply never sent anyone there. Measured
 * on a phone: a parent gains **8px** over the screen she used to land on and a
 * teacher gains nothing, so the depth objection that kept the split alive does
 * not survive the measurement (D-384).
 *
 * ***And this is why the routing no longer asks whether she teaches.*** That
 * question had two answers — here it was the `TakeAttendance` capability, while
 * `HomeScreen`, `ScheduleScreen`, `ViewBar`, `nav.ts` and `TodayForMe` all read
 * the `teaches` person-link. They disagree for a teacher between seasons, who
 * holds the capability and instructs nothing, and she was routed to the blank
 * screen. **The fix is that the question is gone rather than reconciled**: one
 * composed home means routing never needs to know, and the composition reads
 * the link like everything else does.
 *
 * **Read most-privileged first**, which is D-128 unchanged: whoever runs the
 * studio gets the studio's own screen, because it is built for running one
 * rather than teaching in one. That rung is the only one left.
 *
 * ⚠ **This used to send her to the day calendar, and the reasoning moved with
 * her** (D-878). D-599 settled *the day, not the week* — `asked`, *"for Tonya's
 * calendar view let's default it to day view"* — on the premise that *her home
 * **is** the calendar*. **That premise is what changed**: her home is now what
 * needs her, and the calendar is the next entry in the rail. D-599 still
 * governs `/calendar`, which still redirects to the day; what no longer holds
 * is the part of it that was about landing.
 *
 * Nothing here takes a date any more, which is why the parameter went: the
 * dashboard has no day in its address.
 */
export function landingFor(subject: NavSubject): string {
  /*
   * **What needs her, then the timetable** (D-878). D-176 sent whoever runs the
   * studio to the day calendar *"until the dashboard the proposal mocked up
   * exists"* - it exists now, so this is the swap that sentence was waiting for.
   *
   * **The same capability, deliberately.** `ViewAllSchedules` is what
   * `/api/dashboard` itself asks for, so the landing and the endpoint cannot
   * drift into disagreeing about who belongs here. Each row is gated again on
   * the server by what its own data needs, which is why an admin landing here
   * is correct: she gets the operations rows and not the owner's feedback
   * queue, rather than a different screen.
   *
   * A teacher does not hold it and still lands on `/home`, where `TodayForMe`
   * answers the same question scoped to her - two answers to *what needs me*
   * would drift, and the scoped one is hers.
   *
   * The calendar is one tap away in the rail, under Home - which is the row that lights here, because this screen has none of its own (D-884).
   */
  if (subject.capabilities.includes('ViewAllSchedules')) return '/dashboard'

  return '/home'
}
